import classNames from 'classnames';
import {RemoveIcon, VerticalSpacer} from 'epic-ue-ui';
import React from 'react';
import {Field} from 'react-final-form';
import styled from 'styled-components';
import CustomSelect from '../formElements/inputFields/CustomSelect';
import FileField from '../formElements/inputFields/FileField';
import InputLabel from '../formElements/inputFields/InputLabel';
import InputValidationErrorMsg from '../formElements/inputFields/InputValidationErrorMsg';
import {
    isFilePlaintextOptionOpen,
    isMultiSelect,
    mapFieldType,
    required,
    toggleFileOption,
    toggleFilePlaintextOption
} from '../jobForm.utils';
import {withLocalization} from '@epic-core/localization';

const Styled = styled.div`
    .job-form-text-field-container {
        position: relative;
        .ue-icon-remove {
            width: 20px;
            height: auto;
            cursor: pointer;
            position: absolute;
            right: 0;
            top: 10px;
        }
        textarea {
            resize: vertical;
        }
    }
`;

export interface QuestionsBlockProps {
    keyProp?: string;
    questions?: any[];
    formRenderProps: {
        handleSubmit;
        hasValidationErrors;
        submitFailed;
        form;
        submitSucceeded;
        values;
    };
    enterManuallyButtonlabel?: string;
    cancelEnterManuallyButtonLabel?: string;
    isInputsDisabled?: boolean;
    sectionRequiredLabel?: string;
    uploadFileValidationLabel?: string;
    sectionHeading?: string;
    getMessage: (key: string) => string;
}

const QuestionsBlock = ({
    getMessage,
    keyProp,
    questions = [],
    formRenderProps,
    enterManuallyButtonlabel = getMessage('epic.jobs.form.enterManually'),
    isInputsDisabled,
    sectionRequiredLabel,
    uploadFileValidationLabel = getMessage('epic.jobs.form.uploadFileValidation')
}: QuestionsBlockProps): JSX.Element => {
    return (
        <Styled className="questions">
            {questions?.map((question, i) => {
                const firstFieldType = mapFieldType(question?.fields[0]?.type);

                const questionLabel = (
                    <InputLabel label={question?.label} required={question?.required}></InputLabel>
                );

                /*Resume as cover letter need to be handled differently we validate them checking two seperate fields (file upload and textarea) */
                const isResumeOrCoverLetter =
                    question?.fields[0]?.name === 'resume' ||
                    question?.fields[0]?.name === 'cover_letter';

                if (question?.fields[0]?.name === 'resume' && question?.required) {
                    formRenderProps.values.resumeRequired = true;
                }
                if (question?.fields[0]?.name === 'cover_letter' && question?.required) {
                    formRenderProps.values.coverLetterRequired = true;
                }

                const labelAlphaNumeric = question?.label?.replace(/\W/g, '');

                let hasEnterManuallyInput = false;

                return (
                    <div
                        key={`${i}-${keyProp}`}
                        className={classNames(
                            'field-group',
                            {
                                dropdown: firstFieldType === 'select'
                            },
                            {
                                file: question?.fields[0]?.type === 'input_file'
                            },
                            {
                                'resume-or-cover': isResumeOrCoverLetter
                            }
                        )}>
                        {questionLabel}

                        {question?.description && (
                            <div
                                className="field-description"
                                dangerouslySetInnerHTML={{
                                    __html: question?.description
                                }}
                            />
                        )}
                        {question?.fields.map((field, j) => {
                            if (isResumeOrCoverLetter && field?.name.match(/_text$/)) {
                                hasEnterManuallyInput = true;
                            }

                            if (field?.type === 'input_file') {
                                return (
                                    <div key={`${i}-${j}-file-${field?.name}`}>
                                        <div className="field-description">
                                            <p>(File types: pdf, doc, docx, txt, rtf)</p>
                                        </div>
                                        <div
                                            key={j}
                                            className={classNames(field.name, labelAlphaNumeric, {
                                                'resumeOrCoverLetter-file': isResumeOrCoverLetter
                                            })}>
                                            <Field key={`${i}-${j}`} name={`file.${field?.name}`}>
                                                {({meta}) => (
                                                    <>
                                                        <div
                                                            className={classNames({
                                                                'validate-error':
                                                                    !formRenderProps?.submitSucceeded &&
                                                                    meta.error &&
                                                                    meta.touched
                                                            })}>
                                                            {!formRenderProps?.submitSucceeded &&
                                                                meta.error &&
                                                                meta.touched && (
                                                                    <InputValidationErrorMsg
                                                                        error={
                                                                            !isResumeOrCoverLetter &&
                                                                            meta.error ===
                                                                                'Required'
                                                                                ? uploadFileValidationLabel
                                                                                : getMessage(
                                                                                      meta.error
                                                                                  )
                                                                        }
                                                                        sectionRequiredLabel={
                                                                            sectionRequiredLabel
                                                                        }
                                                                    />
                                                                )}
                                                            <FileField
                                                                key={`${i}-${j}`}
                                                                name={`file.${field?.name}`}
                                                                isDisabled={isInputsDisabled}
                                                                meta={meta}
                                                                labelAlphaNumeric={
                                                                    labelAlphaNumeric
                                                                }
                                                                isRequired={
                                                                    isResumeOrCoverLetter
                                                                        ? !isFilePlaintextOptionOpen(
                                                                              labelAlphaNumeric
                                                                          ) && question?.required
                                                                        : question?.required
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </Field>
                                        </div>
                                    </div>
                                );
                            } else {
                                const fieldType = mapFieldType(field?.type);

                                return fieldType === 'select' ? (
                                    <div key={`${i}-${j}-select-${field?.name}`}>
                                        <CustomSelect
                                            key={`${i}-${j}`}
                                            name={`questions.${field?.name}`}
                                            options={field?.values}
                                            isRequired={question?.required}
                                            isDisabled={isInputsDisabled}
                                            isMultiSelect={isMultiSelect(field?.type)}
                                            labelPropName="label"
                                            valuePropName="value"
                                            isAutocomplete={isMultiSelect(field?.type)}
                                            placeholder={getMessage('epic.jobs.form.select')}
                                            sectionRequiredLabel={sectionRequiredLabel}
                                        />
                                    </div>
                                ) : (
                                    fieldType && (
                                        <div key={`${i}-${j}-${field?.name}`}>
                                            <Field
                                                key={`${i}-${j}`}
                                                name={`questions.${field?.name}`}>
                                                {({meta}) => (
                                                    <div
                                                        className={classNames(
                                                            'job-form-text-field-container',
                                                            field.name,
                                                            labelAlphaNumeric,
                                                            {
                                                                'resumeOrCoverLetter-text':
                                                                    isResumeOrCoverLetter
                                                            },
                                                            {
                                                                hidden: isResumeOrCoverLetter
                                                            }
                                                        )}>
                                                        <Field
                                                            key={`${i}-${j}`}
                                                            type="text"
                                                            placeholder={getMessage(
                                                                'epic.jobs.form.enter'
                                                            )}
                                                            disabled={isInputsDisabled}
                                                            className={classNames({
                                                                'validate-error':
                                                                    !formRenderProps?.submitSucceeded &&
                                                                    meta.error &&
                                                                    meta.touched
                                                            })}
                                                            validate={
                                                                (
                                                                    isResumeOrCoverLetter
                                                                        ? isFilePlaintextOptionOpen(
                                                                              labelAlphaNumeric
                                                                          ) && question?.required
                                                                        : question?.required
                                                                )
                                                                    ? required
                                                                    : undefined
                                                            }
                                                            name={`questions.${field?.name}`}
                                                            component={mapFieldType(field?.type)}
                                                        />
                                                        {isResumeOrCoverLetter && (
                                                            <RemoveIcon
                                                                onClick={() =>
                                                                    toggleFileOption(
                                                                        labelAlphaNumeric
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                        {!formRenderProps?.submitSucceeded &&
                                                            meta.error &&
                                                            meta.touched && (
                                                                <InputValidationErrorMsg
                                                                    error={getMessage(meta.error)}
                                                                    sectionRequiredLabel={
                                                                        sectionRequiredLabel
                                                                    }
                                                                />
                                                            )}
                                                    </div>
                                                )}
                                            </Field>
                                        </div>
                                    )
                                );
                            }
                        })}

                        {/* Button to open text area for Resume / Cover letter input sections  */}
                        {isResumeOrCoverLetter && (
                            <>
                                {hasEnterManuallyInput && (
                                    <div className="fileUploadOptions">
                                        <a
                                            role="button"
                                            tabIndex={0}
                                            className={classNames(
                                                labelAlphaNumeric,
                                                'resumeOrCoverLetter-manuallyEnter'
                                            )}
                                            onClick={() => {
                                                toggleFilePlaintextOption(labelAlphaNumeric);
                                                return false;
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    toggleFilePlaintextOption(labelAlphaNumeric);
                                                    return false;
                                                }
                                            }}>
                                            {enterManuallyButtonlabel}
                                        </a>
                                    </div>
                                )}
                            </>
                        )}

                        <VerticalSpacer variant="eyebrow" />
                    </div>
                );
            })}

            {/* Dummy Horizontal Spacer Div to make sure odd number columns always align */}
            <div className={classNames('field-group', 'spacer-div')}></div>
        </Styled>
    );
};

export default withLocalization(QuestionsBlock);
